import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-sm border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <span className="text-xl font-heading font-bold text-primary-500">
            韩漫网
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              isActive('/')
                ? 'bg-primary-100 text-primary-700'
                : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
            }`}
          >
            {t('common.home')}
          </Link>

          <div className="relative group">
            <Link
              to="/category/all"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1 ${
                location.pathname.startsWith('/category')
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
              }`}
            >
              {t('common.category')}
              <i className="ri-arrow-down-s-line"></i>
            </Link>

            <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-2 shadow-sm min-w-[180px]">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors whitespace-nowrap"
                  >
                    <i className={`${cat.icon} text-base text-primary-500`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <Link
            to="/about"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              isActive('/about')
                ? 'bg-primary-100 text-primary-700'
                : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
            }`}
          >
            {t('common.about')}
          </Link>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors"
          aria-label="菜单"
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70 px-4 pb-4">
          <Link
            to="/"
            className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
              isActive('/')
                ? 'bg-primary-100 text-primary-700'
                : 'text-foreground-700 hover:bg-background-100'
            }`}
          >
            {t('common.home')}
          </Link>

          <Link
            to="/category/all"
            className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
              location.pathname.startsWith('/category')
                ? 'bg-primary-100 text-primary-700'
                : 'text-foreground-700 hover:bg-background-100'
            }`}
          >
            {t('common.category')}
          </Link>

          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="flex items-center gap-2 px-4 pl-8 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors"
            >
              <i className={`${cat.icon} text-base text-primary-500`}></i>
              {cat.name}
            </Link>
          ))}

          <Link
            to="/about"
            className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
              isActive('/about')
                ? 'bg-primary-100 text-primary-700'
                : 'text-foreground-700 hover:bg-background-100'
            }`}
          >
            {t('common.about')}
          </Link>
        </div>
      )}
    </nav>
  );
}